import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, PlayCircle } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Hero3DScene } from './Hero3DScene';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.15 + i * 0.08, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const HeroSection = () => {
  const wrapRef = useRef(null);
  const sceneRef = useRef(null);
  const textRef = useRef(null);

  // GSAP scroll-pinned timeline: as user scrolls past hero, 3D scene fades & lifts; text gently parallaxes down
  useGSAP(() => {
    if (!wrapRef.current) return;
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapRef.current,
        start: 'top top',
        end: '+=80%',
        scrub: 0.8,
      },
    });
    tl.to(sceneRef.current, { y: -60, scale: 0.94, opacity: 0.55, ease: 'none' }, 0);
    tl.to(textRef.current, { y: 50, opacity: 0.0, ease: 'none' }, 0);
  }, { scope: wrapRef });

  return (
    <section
      ref={wrapRef}
      id="hero"
      data-testid="landing-hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#28282B]"
    >
      {/* The 3D scene fills the section as background. On mobile the iPad
          itself is hidden inside the Canvas (see Hero3DScene) — only the
          floating receipts render — so the canvas stays mounted at all
          breakpoints. */}
      <div ref={sceneRef} className="absolute inset-0 z-0">
        <Hero3DScene />
      </div>

      {/* Mobile-only readability scrim — a soft dark wash on top of the
          receipts so the headline and body text stay crisp. Desktop is
          unaffected (the iPad fills the right half, the text sits over a
          quieter portion of the canvas). */}
      <div
        aria-hidden="true"
        className="md:hidden pointer-events-none absolute inset-0 z-[2]"
        style={{
          background:
            'linear-gradient(180deg, rgba(15,11,10,0.35) 0%, rgba(15,11,10,0.55) 38%, rgba(15,11,10,0.55) 70%, rgba(15,11,10,0.25) 100%)',
        }}
      />

      {/* Grain texture overlay — gives a tactile, "human-made" feel */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Vignette — pulls focus toward the text */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 30%, rgba(8,8,10,0.65) 90%)',
        }}
      />

      {/* Editorial overlay text */}
      <div
        ref={textRef}
        className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 w-full grid lg:grid-cols-12 gap-8 py-24"
      >
        <motion.div
          initial="hidden"
          animate="visible"
          className="lg:col-span-7 max-w-4xl"
        >
          {/* Eyebrow */}
          <motion.div variants={fadeUp} className="flex items-center gap-3 text-[10px] md:text-xs uppercase tracking-[0.32em] text-white/55 font-semibold mb-8">
            <span className="block w-8 h-px bg-[#C9A66B]" />
            Epicurea Nexus · Mumbai
          </motion.div>

          {/* Headline with hand-drawn squiggle accent */}
          <motion.h1
            variants={fadeUp}
            className="font-display text-white font-semibold tracking-tight leading-[0.95] text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[5rem]"
          >
            Restaurant financials,
            <br />
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#A8D5BA] via-[#E8D29A] to-[#C9A66B]">
              read like a briefing.
            </span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-7 md:mt-9 text-white/65 text-base md:text-xl max-w-2xl leading-relaxed"
          >
            An in-house finance practice for restaurants — deterministic movement bridges, AI-written narratives that never invent a number, and a reconciliation quietly running in the background. The recoveries we surface along the way pay for the read.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={fadeUp} custom={3} className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href="#contact"
              data-testid="landing-hero-cta"
              className="group relative inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#28282B] font-semibold px-7 py-3.5 text-base hover:bg-white/90 transition-all duration-300 shadow-[0_8px_32px_rgba(255,255,255,0.18)] overflow-hidden"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Book a Diagnostic
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
              <span aria-hidden="true" className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-[#C9A66B]/35 to-transparent" />
            </a>
            <a
              href="#approach"
              data-testid="landing-hero-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/15 text-white font-medium px-7 py-3.5 text-base hover:bg-white/[0.08] hover:border-white/25 transition-all duration-300"
            >
              <PlayCircle className="w-4 h-4" />
              See how it works
            </a>
          </motion.div>

          {/* Tiny proof bar */}
          <motion.div
            variants={fadeUp}
            custom={4}
            className="mt-12 md:mt-16 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/40 text-xs md:text-sm"
          >
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#A8D5BA]"/>Petpooja sync</span>
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#E8D29A]"/>Tally connector</span>
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9A66B]"/>Swiggy · Zomato audit</span>
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#A8D5BA]"/>Live mandi prices</span>
          </motion.div>
        </motion.div>
      </div>

      {/* Subtle scroll cue — animated arrow at bottom centre */}
      <div aria-hidden="true" className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40">
        <motion.span
          animate={{ y: [0, 6, 0], opacity: [0.4, 0.9, 0.4] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          className="block w-px h-10 bg-white/30"
        />
      </div>
    </section>
  );
};

export default HeroSection;
